"use client";

import { Button } from "@heroui/button";

import type { LanguageCode } from "@/config/i18n";
import type { Movie, MovieFilters, MovieSortOptions } from "@/types/movie";
import { FilterPanel } from "@/components/movies/FilterPanel";
import { MovieGrid } from "@/components/movies/MovieGrid";
import { SearchBar } from "@/components/movies/SearchBar";
import { SortControl } from "@/components/movies/SortControl";
import { createTranslator } from "@/lib/i18n/translations";
import { clearFilters, hasActiveFilters } from "@/lib/movies/browse-params";

/** Set on <html> while a server-rendered fallback waits for URL filters. */
export const PENDING_ATTRIBUTE = "data-browse-pending";

export interface MoviesViewProps {
  lang: LanguageCode;
  /** Results to show (already filtered and sorted). */
  movies: Movie[];
  /** Number of browsable movies before filtering. */
  totalCount: number;
  availableGenres: string[];
  yearRange: { min: number; max: number };
  filters: MovieFilters;
  sortOptions: MovieSortOptions;
  onSearchChange?: (search: string) => void;
  onFiltersChange?: (filters: MovieFilters) => void;
  onSortChange?: (sort: MovieSortOptions) => void;
  /**
   * Static, server-rendered stand-in shown until the client applies the URL's
   * search/filters. When the URL has a query, the unfiltered results are
   * hidden before first paint (skeleton instead) so nothing jumps around.
   */
  isFallback?: boolean;
}

const noop = () => {};

/** Runs before first paint (fallback HTML only): flag URLs with a query. */
const PENDING_SCRIPT = `if(location.search.length>1)document.documentElement.setAttribute("${PENDING_ATTRIBUTE}","")`;

/** The browse page UI (search, filters, sort and the results grid). */
export function MoviesView({
  lang,
  movies,
  totalCount,
  availableGenres,
  yearRange,
  filters,
  sortOptions,
  onSearchChange = noop,
  onFiltersChange = noop,
  onSortChange = noop,
  isFallback = false,
}: MoviesViewProps) {
  const t = createTranslator(lang);
  const filtersActive = hasActiveFilters(filters);
  // Fallback only: hidden while <html data-browse-pending> is set.
  const pendingClassName = isFallback
    ? "[[data-browse-pending]_&]:invisible"
    : "";

  return (
    <div className="flex w-full flex-col gap-4 pb-8 md:gap-6">
      {isFallback && typeof window === "undefined" && (
        <script dangerouslySetInnerHTML={{ __html: PENDING_SCRIPT }} />
      )}

      {/* Page Header */}
      <div>
        <h1 className="mb-1 text-3xl font-bold sm:mb-2 sm:text-4xl">
          {t("movies.title")}
        </h1>
        <p className="text-default-600">
          {t("movies.description", { count: totalCount })}
        </p>
      </div>

      {/* Phones: search, then one row with filters + sort. md+: one row.
          The static fallback's controls are inert until the page hydrates. */}
      <fieldset
        className="flex w-full min-w-0 flex-col gap-3 md:flex-row md:items-center md:gap-4"
        disabled={isFallback}
      >
        <div className="min-w-0 md:flex-1">
          <SearchBar
            label={t("movies.search")}
            placeholder={t("movies.search")}
            value={filters.search ?? ""}
            onChange={onSearchChange}
          />
        </div>

        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <FilterPanel
            availableGenres={availableGenres}
            className="shrink-0"
            filters={filters}
            lang={lang}
            resultCount={movies.length}
            yearRange={yearRange}
            onChange={onFiltersChange}
          />
          <div className="min-w-0 flex-1 md:flex-none">
            <SortControl
              lang={lang}
              sortOptions={sortOptions}
              onChange={onSortChange}
            />
          </div>
        </div>
      </fieldset>

      {/* Movie count, and a way to clear filters when any are active */}
      <div
        className={`flex min-h-11 flex-wrap items-center justify-between gap-x-3 ${pendingClassName}`}
      >
        <p aria-live="polite" className="text-sm text-default-600">
          {t("movies.resultCount", { count: movies.length })}
        </p>
        {filtersActive && (
          <Button
            className="min-h-11"
            color="danger"
            size="sm"
            variant="light"
            onPress={() => onFiltersChange(clearFilters(filters))}
          >
            {t("movies.clearFilters")}
          </Button>
        )}
      </div>

      {isFallback ? (
        <div className="relative">
          <div className={pendingClassName}>
            <MovieGrid
              lang={lang}
              movies={movies}
              sortBy={sortOptions.sortBy}
            />
          </div>
          <div className="absolute inset-x-0 top-0 hidden [[data-browse-pending]_&]:block">
            <MovieGrid isLoading lang={lang} movies={[]} />
          </div>
        </div>
      ) : (
        <MovieGrid lang={lang} movies={movies} sortBy={sortOptions.sortBy} />
      )}
    </div>
  );
}
